

























































































































import {
  faCamera,
  faCheck,
  faExclamationTriangle,
  faFile,
  faPencilAlt,
  faPlus,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons'
import {
  defineComponent,
  ref,
  PropType,
  toRefs,
  watch,
  computed,
  nextTick
} from '@nuxtjs/composition-api'

import { useDraggable, useDroppable } from '~/compositions/cursor'
import { useFileUpload } from '~/compositions/files/upload'
import { useI18n } from '~/compositions/i18n'
import { UploaderFile } from '~/models/photos/uploader'
import CDynamicIcon from '~/components/shared/configurable/icon/CDynamicIcon.vue'
import CFileUploaderEntry from '~/components/shared/configurable/file/CFileUploaderEntry.vue'
import CDraggable from '~/components/shared/configurable/CDraggable.vue'
import CFloatingButton from '~/components/shared/configurable/CFloatingButton.vue'
import { isElementInViewport } from '~/utils/dom'
import { ciPlus } from '~/icons/source/solid/plus'
import { FileUploaderForType } from '~/models/form/file-uploader'
import { defineComponentTranslations } from '~/utils/i18n'
import { ci360Logo } from '~/icons/source/solid/360logo'
import { ciImageUpload } from '~/icons/source/solid/image-upload'
import { useUserAgent } from '~/compositions/user-agent'
import { ciAngleDown } from '~/icons/source/regular/angle-down'
import { ciAngleUp } from '~/icons/source/regular/angle-up'
import { useAppConfirmationModal } from '~/compositions/app-confirmation-modal'
import { ConfirmationModalOptions } from '~/store/modules/shared/app-confirmation-modal/state'

export default defineComponent({
  components: { CFloatingButton, CDraggable, CDynamicIcon, CFileUploaderEntry },
  props: {
    acceptedFileTypes: {
      type: Array as PropType<string[]>,
      default() {
        return []
      }
    },
    max: {
      type: Number,
      default: 60
    },
    loading: {
      type: Boolean,
      default: false
    },
    overlayOpacity: {
      type: Number,
      default: 1
    },
    files: {
      type: Array as PropType<UploaderFile[]>,
      default: () => []
    },
    forType: {
      type: String as PropType<FileUploaderForType>,
      required: false,
      default: 'image'
    },
    placeholderText: {
      type: String,
      default() {
        return this.$t('drop your files or click here').toString()
      }
    },
    filesHaveTitles: {
      type: Boolean,
      default: false
    },
    titleInputPlaceholder: {
      type: String,
      default: null
    },
    showFloatingButton: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props, { emit }) {
    const { acceptedFileTypes, max, loading, files, forType } = toRefs(props)
    const dropInputTemplateRef = ref<HTMLElement | null>(null)
    const internalFiles = ref<UploaderFile[]>(files.value)
    const internalLoading = ref(loading.value)
    const draggableRef = ref(null)
    const expanded = ref(false)

    const isVisible = ref(false)
    const { dragging, onDragStart, onDragEnd } = useDraggable()
    const { droppingOver, onDrop, onDragOver, onDragLeave } = useDroppable(
      dragging,
      fileDropped
    )
    const { filesAreOfAcceptableType } = useFileUpload()
    const { tc, t } = useI18n()
    const { isMobile } = useUserAgent()
    const confirm = useAppConfirmationModal()

    const expandLimit = computed(() => {
      // move this to props if needed
      return isMobile.value ? 4 : 5
    })

    const internalFilesToShow = computed(() => {
      if (expanded.value) {
        return internalFiles.value
      }

      return [...internalFiles.value.slice(0, expandLimit.value)]
    })

    function addFiles(fileList: FileList) {
      if (!fileList || !fileList.length) {
        return
      }
      // Uploaded files are above max length
      if (fileList.length + internalFiles.value.length > max.value) {
        return alert(
          tc(
            'too many attachments, maximum is 1 file | too many attachments, maximum is {count} files',
            max.value
          )
        )
      }
      if (
        acceptedFileTypes.value?.length &&
        !filesAreOfAcceptableType(fileList, acceptedFileTypes.value)
      ) {
        return alert(t('this file type is not supported'))
      }

      emit('add', Array.from(fileList))

      if (expanded.value === false) {
        toggleExpanded()
      }
    }
    function fileInputChanged(e: Event) {
      addFiles((e.target as any).files)
    }
    function fileDropped(e: DragEvent) {
      addFiles((e.dataTransfer as any).files)
    }
    function orderChanged() {
      emit('change', internalFiles.value)
    }
    function handleDeleteFile({
      name,
      id,
      file
    }: {
      name: string
      id: string
      file: string
    }) {
      let deleteFileMsg

      switch (forType.value) {
        case 'image':
        case '360':
          deleteFileMsg = t('photo_delete_confirm')
          break
        default:
          deleteFileMsg = t('file_delete_confirm')
          break
      }

      confirm.show({
        variant: 'danger',
        title: t('delete'),
        text: deleteFileMsg,
        confirmButtonText: t('delete'),
        centered: true,
        size: 'md',
        onConfirm: () => {
          emit('delete', { name, id, file })
        }
      } as ConfirmationModalOptions)
    }
    function handleFileTitleChange(name: string, title: string) {
      emit('title-change', { name, title })
    }
    function triggerInput(): void {
      if (dropInputTemplateRef.value) {
        dropInputTemplateRef.value.value = null
        dropInputTemplateRef.value.click()
      }
    }

    watch(loading, newLoading => (internalLoading.value = newLoading))
    watch(files, newPhotos => (internalFiles.value = newPhotos))

    const icons = {
      file: faFile,
      plus: faPlus,
      pencilAlt: faPencilAlt,
      exclamationTriangle: faExclamationTriangle,
      trash: faTrash,
      checkMark: faCheck,
      xMark: faTimes
    }

    const addText = computed(() => {
      switch (forType.value) {
        case 'image':
        case '360':
          return t('add_photo')
        default:
          return t('add_file')
      }
    })

    const triggerText = computed(() => {
      switch (forType.value) {
        case '360':
        case 'image':
          return {
            desktop: t('drag_your_photos'),
            mobile: t('for_your_photos')
          }
        default:
          return { desktop: t('drag_your_files'), mobile: t('for_your_files') }
      }
    })

    const placeholderIcon = computed(() => {
      switch (forType.value) {
        case '360':
          return ci360Logo
        case 'image':
        default:
          return ciImageUpload
      }
    })

    const viewAllText = computed(() => {
      switch (forType.value) {
        case '360':
        case 'image':
          return t('view_all_photos')
        default:
          return t('view_all_files')
      }
    })

    const viewLessText = computed(() => {
      switch (forType.value) {
        case '360':
        case 'image':
          return t('view_less_photos')
        default:
          return t('view_less_files')
      }
    })

    const hasImages = computed(() => internalFiles.value.length > 0)

    const visibilityChanged = (visible: boolean) => {
      isVisible.value = visible
    }

    const scrollNotVisibleFileAtIndex = async (index: number) => {
      if (!process.client) {
        return
      }
      await nextTick()
      if (
        draggableRef.value?.$el?.childNodes[index] &&
        isElementInViewport(draggableRef.value.$el.childNodes[index]) === false
      ) {
        draggableRef.value.$el.childNodes[index].scrollIntoView({
          behavior: 'smooth'
        })
      }
    }

    const toggleExpanded = () => {
      expanded.value = !expanded.value
    }

    return {
      dragging,
      droppingOver,
      onDragStart,
      onDragEnd,
      onDrop,
      onDragOver,
      onDragLeave,
      internalLoading,
      icons,
      fileInputChanged,
      orderChanged,
      handleDeleteFile,
      triggerInput,
      dropInputTemplateRef,
      internalFiles,
      placeholderIcon,
      hasImages,
      handleFileTitleChange,
      faCamera,
      ciPlus,
      visibilityChanged,
      isVisible,
      draggableRef,
      addText,
      triggerText,
      scrollNotVisibleFileAtIndex,
      expanded,
      expandLimit,
      internalFilesToShow,
      ciAngleDown,
      ciAngleUp,
      viewAllText,
      viewLessText,
      toggleExpanded,
      isMobile
    }
  },
  i18n: defineComponentTranslations({
    add_photo: {
      en: 'Add photo',
      el: 'Προσθήκη φωτογραφίας'
    },
    add_file: {
      en: 'Add a file',
      el: 'Προσθήκη αρχείου'
    },
    drag_your_photos: {
      en: 'Drag your photos',
      el: 'Σύρε τις φωτογραφίες σου'
    },
    for_your_photos: {
      en: 'For your photos',
      el: 'Για τις φωτογραφίες σου'
    },
    drag_your_files: {
      en: 'Drag your files',
      el: 'Σύρε τα αρχεία σου'
    },
    for_your_files: {
      en: 'For your files',
      el: 'Για τα αρχεία σου'
    },
    or_click_here: {
      en: 'or click {here}',
      el: 'ή κάνε click {here}'
    },
    press_here: {
      en: 'click {here}',
      el: 'πάτησε {here}'
    },
    view_all_files: {
      en: 'View all files',
      el: 'Δες όλα τα αρχεία'
    },
    view_less_files: {
      en: 'View less files',
      el: 'Δες λιγότερα αρχεία'
    },
    view_all_photos: {
      en: 'View all photos',
      el: 'Δες όλες τις φωτογραφίες'
    },
    view_less_photos: {
      en: 'View less photos',
      el: 'Δες λιγότερες φωτογραφίες'
    },
    file_delete_confirm: {
      en: 'Do you really want to delete this file?',
      el: 'Θέλεις  σίγουρα να διαγράψεις αυτό το αρχείο;'
    },
    photo_delete_confirm: {
      en: 'Do you really want to delete this photo?',
      el: 'Θέλεις  σίγουρα να διαγράψεις αυτή τη φωτογραφία;'
    }
  })
})
